import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import MicRecorder from 'mic-recorder-to-mp3';
import PropTypes from 'prop-types';

import actionCadastrarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionCadastrarRespostaRapida';
import actionEditarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionEditarRespostaRapida';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import { fData } from 'src/utils/formatNumber';
import Iconify from 'src/components/Iconify';
import EmojiPicker from 'src/components/EmojiPicker';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { metaDoTipo, nomeDoArquivo } from './TableRespostasRapidas';
import PreviewRespostaRapida from './PreviewRespostaRapida';
import D from './RespostasRapidas.design';

const LIMITE_GRAVACAO = 180;
const LIMITE_TEXTO = 1024;

const TIPOS = [
  { id: 'texto', label: 'Texto', icon: 'eva:message-square-outline' },
  { id: 'arquivo', label: 'Arquivo', icon: 'eva:attach-2-outline' },
  { id: 'audio', label: 'Áudio', icon: 'eva:mic-outline' },
];

function formatarTempo(segundos) {
  const total = Math.max(0, Math.round(segundos || 0));
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`;
}

export default function DrawerFormRespostaRapida({
  open,
  onClose,
  mode = 'criar',
  initialData = null,
  clienteId,
  onSuccess,
  onRequestDelete,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [salvando, setSalvando] = useState(false);

  const [tituloEdit, setTituloEdit] = useState('');
  const [respostaEdit, setRespostaEdit] = useState('');
  const [tipoResposta, setTipoResposta] = useState('texto');
  const [respostaOriginal, setRespostaOriginal] = useState(null);

  const [files, setFiles] = useState([]);

  const [audioGravado, setAudioGravado] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duracaoAudio, setDuracaoAudio] = useState(0);
  const [recording, setRecording] = useState(false);
  const [tempoRestante, setTempoRestante] = useState(LIMITE_GRAVACAO);

  const timerRef = useRef(null);
  const recordingRef = useRef(false);
  const inicioGravacaoRef = useRef(0);
  const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);

  const limparTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const pararGravacao = useCallback(
    async (descartar = false) => {
      limparTimer();
      if (!recordingRef.current) {
        setRecording(false);
        return;
      }
      recordingRef.current = false;
      try {
        const [buffer, blob] = await recorder.stop().getMp3();
        if (!descartar) {
          const file = new File(buffer, 'audio.mp3', { type: blob.type, lastModified: Date.now() });
          setDuracaoAudio(Math.round((Date.now() - inicioGravacaoRef.current) / 1000));
          setAudioGravado(file);
        }
      } catch (error) {
        console.error('Erro ao finalizar gravação', error);
      } finally {
        setRecording(false);
      }
    },
    [recorder, limparTimer]
  );

  const resetForm = useCallback(() => {
    setTituloEdit('');
    setRespostaEdit('');
    setTipoResposta('texto');
    setFiles([]);
    setAudioGravado(null);
    setDuracaoAudio(0);
    setTempoRestante(LIMITE_GRAVACAO);
    setRespostaOriginal(null);
  }, []);

  const handleClose = useCallback(() => {
    pararGravacao(true);
    resetForm();
    onClose();
  }, [pararGravacao, resetForm, onClose]);

  // Hidrata o formulário ao abrir / trocar mode+data
  useEffect(() => {
    if (!open) return;
    if (mode === 'editar' && initialData) {
      setRespostaOriginal(initialData);
      setTituloEdit(initialData.titulo || '');
      setRespostaEdit(initialData.tipo === 'texto' ? initialData.resposta || '' : '');
      setTipoResposta(initialData.tipo || 'texto');
      setFiles([]);
      setAudioGravado(null);
      setDuracaoAudio(0);
      setTempoRestante(LIMITE_GRAVACAO);
    } else {
      resetForm();
    }
  }, [open, mode, initialData, resetForm]);

  useEffect(() => {
    if (!audioGravado) {
      setAudioUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(audioGravado);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioGravado]);

  const handleStartRecording = async () => {
    setTempoRestante(LIMITE_GRAVACAO);
    setDuracaoAudio(0);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      recordingRef.current = true;
      inicioGravacaoRef.current = Date.now();
      setRecording(true);

      limparTimer();
      timerRef.current = setInterval(() => {
        setTempoRestante((prev) => (prev <= 0 ? 0 : prev - 1));
      }, 1000);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Não conseguimos acessar o microfone. Libere a permissão no navegador e tente de novo.', {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (recording && tempoRestante === 0) pararGravacao(false);
  }, [recording, tempoRestante, pararGravacao]);

  useEffect(() => () => limparTimer(), [limparTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  const handleDropFile = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles.slice(0, 1));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropFile,
    multiple: false,
  });

  async function uploadArquivo(file, tipo) {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await v2_actionUploadS3(formData, '?path=plataforma/resposta-rapida');
      if (!response.data.error) {
        return { key: response.data.data.key, type: file.type, tipo };
      }
    } catch (error) {
      console.error('Erro ao enviar arquivo', error);
    }
    return null;
  }

  const handleSubmit = async () => {
    setSalvando(true);
    try {
      if (mode === 'editar') {
        await actionEditarRespostaRapida({
          id: respostaOriginal._id,
          titulo: tituloEdit,
          resposta: tipoResposta === 'texto' ? respostaEdit : respostaOriginal.resposta || '',
        });
        enqueueSnackbar('Resposta rápida salva.', { variant: 'success' });
      } else {
        const filesUrl = [];
        if (tipoResposta === 'audio' && audioGravado) {
          const enviado = await uploadArquivo(audioGravado, 'audio');
          if (!enviado) throw new Error('upload');
          filesUrl.push(enviado);
        } else if (tipoResposta === 'arquivo' && files.length > 0) {
          const enviado = await uploadArquivo(files[0], 'arquivo');
          if (!enviado) throw new Error('upload');
          filesUrl.push(enviado);
        }

        await actionCadastrarRespostaRapida({
          clienteId,
          titulo: tituloEdit,
          respostaField: respostaEdit,
          tipo: tipoResposta,
          files: filesUrl,
        });
        enqueueSnackbar('Resposta rápida criada.', { variant: 'success' });
      }

      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Não foi possível salvar a resposta rápida. Tente de novo.', { variant: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  const formValido = () => {
    if (!tituloEdit.trim()) return false;
    if (mode === 'editar') return tipoResposta !== 'texto' || Boolean(respostaEdit.trim());
    if (tipoResposta === 'texto') return Boolean(respostaEdit.trim());
    if (tipoResposta === 'arquivo') return files.length > 0;
    if (tipoResposta === 'audio') return Boolean(audioGravado);
    return false;
  };

  const metaAtual = metaDoTipo(tipoResposta);
  const arquivoSelecionado = files[0] || null;
  const nomeArquivoPreview = arquivoSelecionado
    ? arquivoSelecionado.name
    : respostaOriginal
    ? nomeDoArquivo(respostaOriginal)
    : null;

  if (!open) return null;

  return createPortal(
    <div
      className={D.drawerOverlay}
      role="dialog"
      aria-modal="true"
      aria-label="Resposta rápida"
      data-resposta-rapida-drawer
    >
      <div className={D.drawerBackdrop} onClick={handleClose} />

      <div className={D.drawerPanel}>
        <div className={D.drawerHeader}>
          <div className="min-w-0">
            <p className={D.drawerTitle}>
              {mode === 'editar' ? 'Editar resposta rápida' : 'Nova resposta rápida'}
            </p>
            <p className={D.drawerSubtitle}>
              {mode === 'editar'
                ? 'Ajuste o nome e o texto enviado.'
                : 'Escreva uma vez, use em todos os atendimentos.'}
            </p>
          </div>
          <button type="button" onClick={handleClose} className={D.drawerClose} title="Fechar">
            <Iconify icon="eva:close-outline" width={18} />
          </button>
        </div>

        <div className={D.drawerBody}>
          <div className={D.drawerLeft}>
            <div className={D.drawerFieldStack}>
              <div className={D.field}>
                <label className={D.label} htmlFor="rr-nome">
                  Nome
                </label>
                <Input
                  id="rr-nome"
                  value={tituloEdit}
                  onChange={(e) => setTituloEdit(e.target.value)}
                  placeholder="Ex: Saudação inicial"
                  className={D.input}
                />
                <p className={D.hint}>É por este nome que o atendente encontra a mensagem na lista do chat.</p>
              </div>

              <div className={D.field}>
                <label className={D.label}>Tipo de mensagem</label>
                {mode === 'editar' ? (
                  <div className={D.choiceLocked}>
                    <span className={`${D.tableIcon} ${metaAtual.tile}`}>
                      <Iconify icon={metaAtual.icon} width={16} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-[#18181b]">{metaAtual.label}</p>
                      <p className="text-[12px] text-[#a1a1aa]">
                        O tipo não muda depois de criada. Para trocar, exclua e crie outra.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={D.choiceGrid}>
                    {TIPOS.map((t) => {
                      const ativo = tipoResposta === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          aria-pressed={ativo}
                          onClick={() => setTipoResposta(t.id)}
                          className={`${D.choiceCard} ${ativo ? D.choiceCardOn : D.choiceCardOff}`}
                        >
                          <Iconify
                            icon={t.icon}
                            width={18}
                            className={ativo ? 'text-[#6d28d9]' : 'text-[#a1a1aa]'}
                          />
                          <span className={ativo ? D.choiceTitleOn : D.choiceTitleOff}>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {tipoResposta === 'texto' && (
                <div className={D.field}>
                  <div className="flex items-end justify-between gap-3 mb-[7px]">
                    <label className="text-[13px] font-semibold text-[#18181b]" htmlFor="rr-mensagem">
                      Mensagem
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-[#a1a1aa] tabular-nums">
                        {respostaEdit.length}/{LIMITE_TEXTO}
                      </span>
                      <EmojiPicker setValue={setRespostaEdit} alignRight />
                    </div>
                  </div>
                  <Textarea
                    id="rr-mensagem"
                    rows={8}
                    maxLength={LIMITE_TEXTO}
                    value={respostaEdit}
                    onChange={(e) => setRespostaEdit(e.target.value)}
                    placeholder="Olá! Sou da equipe de atendimento. Como posso ajudar?"
                    className={D.textarea}
                  />
                  <p className={D.hint}>O texto é enviado exatamente como está escrito, com as quebras de linha.</p>
                </div>
              )}

              {tipoResposta === 'arquivo' && (
                <div className={D.field}>
                  <label className={D.label}>Arquivo</label>
                  {mode === 'editar' ? (
                    <div className={D.noticeInfo}>
                      <Iconify icon="eva:info-outline" width={17} className="flex-shrink-0 mt-[1px]" />
                      <span>
                        O arquivo {nomeArquivoPreview || 'anexado'} não pode ser trocado. Aqui você renomeia a
                        resposta; para enviar outro arquivo, crie uma nova.
                      </span>
                    </div>
                  ) : arquivoSelecionado ? (
                    <div className={D.fileRow}>
                      <span className={D.fileIcon}>
                        <Iconify icon="eva:file-text-outline" width={17} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className={D.fileName}>{arquivoSelecionado.name}</p>
                        <p className={D.fileMeta}>{fData(arquivoSelecionado.size)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFiles([])}
                        className={D.iconButtonDanger}
                        title="Remover arquivo"
                      >
                        <Iconify icon="eva:trash-2-outline" width={16} />
                      </button>
                    </div>
                  ) : (
                    <div {...getRootProps()} className={`${D.dropzone} ${isDragActive ? D.dropzoneActive : ''}`}>
                      <input {...getInputProps()} />
                      <span className={D.dropzoneIcon}>
                        <Iconify icon="eva:cloud-upload-outline" width={22} />
                      </span>
                      <p className={D.dropzoneTitle}>Arraste um arquivo ou clique para escolher</p>
                      <p className={D.dropzoneHint}>Um arquivo por resposta — PDF, imagem ou documento.</p>
                    </div>
                  )}
                </div>
              )}

              {tipoResposta === 'audio' && (
                <div className={D.field}>
                  <label className={D.label}>Áudio</label>
                  {mode === 'editar' ? (
                    <div className={D.noticeInfo}>
                      <Iconify icon="eva:info-outline" width={17} className="flex-shrink-0 mt-[1px]" />
                      <span>
                        O áudio gravado não pode ser substituído. Aqui você renomeia a resposta; para gravar
                        outro, crie uma nova.
                      </span>
                    </div>
                  ) : (
                    <div className={D.recorderWrap}>
                      {!audioGravado && !recording && (
                        <>
                          <button type="button" onClick={handleStartRecording} className={D.recorderButton}>
                            <Iconify icon="eva:mic-outline" width={24} />
                          </button>
                          <p className={D.dropzoneTitle}>Gravar mensagem de voz</p>
                          <p className={D.dropzoneHint}>Até 3 minutos. Você ouve antes de salvar.</p>
                        </>
                      )}

                      {recording && (
                        <>
                          <p className={tempoRestante <= 30 ? D.recorderTimerWarn : D.recorderTimer}>
                            {formatarTempo(LIMITE_GRAVACAO - tempoRestante)}
                          </p>
                          <p className={D.recorderStatus}>
                            <span className={D.recorderDot} />
                            {tempoRestante <= 30
                              ? `Restam ${formatarTempo(tempoRestante)} de gravação`
                              : 'Gravando'}
                          </p>
                          <div className="flex items-center gap-4 pt-1">
                            <button
                              type="button"
                              onClick={() => pararGravacao(true)}
                              className={D.recorderStop}
                              title="Descartar"
                            >
                              <Iconify icon="eva:close-outline" width={20} />
                            </button>
                            <button
                              type="button"
                              onClick={() => pararGravacao(false)}
                              className={D.recorderKeep}
                              title="Concluir gravação"
                            >
                              <Iconify icon="eva:checkmark-outline" width={20} />
                            </button>
                          </div>
                        </>
                      )}

                      {audioGravado && !recording && (
                        <div className="w-full flex items-center gap-3">
                          {audioUrl && (
                            <audio controls className="w-full h-9" src={audioUrl}>
                              Seu navegador não reproduz áudio.
                            </audio>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setAudioGravado(null);
                              setDuracaoAudio(0);
                              setTempoRestante(LIMITE_GRAVACAO);
                            }}
                            className={D.iconButtonDanger}
                            title="Gravar de novo"
                          >
                            <Iconify icon="eva:trash-2-outline" width={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={D.drawerRight}>
            <p className={`${D.sectionHeaderTitle} mb-4`}>Como o cliente recebe</p>
            <PreviewRespostaRapida
              tipo={tipoResposta}
              titulo={tituloEdit}
              texto={respostaEdit}
              nomeArquivo={nomeArquivoPreview}
              duracaoAudio={duracaoAudio}
              temAudio={Boolean(audioGravado) || (mode === 'editar' && tipoResposta === 'audio')}
            />
          </div>
        </div>

        <div className={D.drawerFooter}>
          <div>
            {mode === 'editar' && onRequestDelete && (
              <button
                type="button"
                onClick={() => {
                  const alvo = respostaOriginal._id;
                  handleClose();
                  onRequestDelete(alvo);
                }}
                className={D.dangerButton}
              >
                <Iconify icon="eva:trash-2-outline" width={16} />
                Excluir
              </button>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            <button type="button" onClick={handleClose} className={D.ghostButton}>
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!formValido() || salvando}
              className={D.primaryButton}
            >
              {salvando && <Iconify icon="eva:loader-outline" width={16} className="animate-spin" />}
              {mode === 'editar' ? 'Salvar alterações' : 'Criar resposta'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

DrawerFormRespostaRapida.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  mode: PropTypes.oneOf(['criar', 'editar']),
  initialData: PropTypes.object,
  clienteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
  onRequestDelete: PropTypes.func,
};
